import { Injectable } from '@angular/core';
import { parse } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class ConvertDateUtcTimezoneService {
  constructor() {}

  convertUTCToLocalTimeDDMMYYYY(
    utcDate: Date | string | undefined,
    timeZone: string
  ): string | undefined {
    if (!timeZone) return undefined;
    if (!utcDate || !timeZone.startsWith('UTC')) return undefined;

    let dateObject: Date;

    // Parse string manually if it's not a Date instance
    if (typeof utcDate === 'string') {
      // Parse the format: dd/MM/yyyy hh:mm:ss a (e.g. 26/06/2025 12:19:00 am)
      dateObject = parse(utcDate, 'dd/MM/yyyy hh:mm:ss a', new Date());
    } else {
      dateObject = utcDate;
    }

    if (isNaN(dateObject.getTime())) return undefined; // Check if date is valid

    const match = timeZone.match(/UTC([+-]\d{1,2})(?::(\d{2}))?/);
    if (!match) return undefined;

    const hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const offsetInMs =
      (hours * 60 + (hours < 0 ? -minutes : minutes)) * 60 * 1000;

    const localDate = new Date(dateObject.getTime() + offsetInMs);

    return localDate.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  convertUTCToLocalTime(
    utcDate: Date | string | undefined,
    timeZone: string | undefined
  ): string | undefined {
    
    if (!utcDate) return undefined;

    // Default to UTC+00:00 if timeZone is not provided or invalid
    timeZone = timeZone && timeZone.startsWith('UTC') ? timeZone : 'UTC+00:00';

    // Ensure utcDate is a Date object. Some APIs return non-ISO strings like
    // "26/06/2025 12:19:00 am" which `new Date(...)` cannot parse reliably.
    let dateObject: Date;
    if (utcDate instanceof Date) {
      dateObject = utcDate;
    } else {
      dateObject = new Date(utcDate);
      if (isNaN(dateObject.getTime()) && typeof utcDate === 'string') {
        // Fallback to dd/MM/yyyy hh:mm:ss a used elsewhere in the app.
        dateObject = parse(utcDate, 'dd/MM/yyyy hh:mm:ss a', new Date());
      }
    }
    if (isNaN(dateObject.getTime())) return undefined; // Check if dateObject is valid

    // Match UTC±hh[:mm] format
    const match = timeZone.match(/UTC([+-]\d{1,2})(?::(\d{2}))?/);
    if (!match) return undefined;

    const hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;

    const offsetInMs =
      (hours * 60 + (hours < 0 ? -minutes : minutes)) * 60 * 1000;

    const localDate = new Date(dateObject.getTime() + offsetInMs);

    return localDate.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  convertLocalTimeToUTC(
    localDate: Date | string | undefined,
    timeZone: string
  ): Date | undefined {
    if (!localDate || !timeZone.startsWith('UTC')) return undefined;

    // Ensure localDate is a Date object
    const dateObject =
      localDate instanceof Date ? localDate : new Date(localDate);
    if (isNaN(dateObject.getTime())) return undefined;

    // Match "UTC±hh[:mm]" format (handles UTC+05 and UTC+05:30)
    const match = timeZone.match(/UTC([+-]\d{1,2})(?::(\d{2}))?/);
    if (!match) return undefined;

    const hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;

    // Calculate total offset in milliseconds
    const offsetInMs =
      (hours * 60 + (hours < 0 ? -minutes : minutes)) * 60 * 1000;

    // Convert local date-time to UTC by subtracting the offset
    const utcDate = new Date(dateObject.getTime() - offsetInMs);

    // Return the UTC date in ISO format or a formatted string
    return utcDate; // You could use `toLocaleString` if needed
  }
}
